import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { CheckCircle, XCircle, Clock, Store, MapPin, Phone, Mail, User, Calendar, AlertCircle, Search, Eye, Edit, Save, X, BarChart3, Package, Trash2, ExternalLink } from 'lucide-react'
import toast from 'react-hot-toast'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface PendingStore {
  id: string
  name: string
  description: string
  address: string
  phone: string
  email: string
  latitude: number
  longitude: number
  offers_kitchen_service: boolean
  offers_delivery: boolean
  delivery_fee: number
  min_order_amount: number
  created_at: string
  owner: {
    name: string
    email: string
    phone: string
  }
}

const AdminStoreApproval = () => {
  const navigate = useNavigate()
  const [stores, setStores] = useState<PendingStore[]>([])
  const [allStores, setAllStores] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedStore, setSelectedStore] = useState<PendingStore | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [showOrdersModal, setShowOrdersModal] = useState(false)
  const [storeStats, setStoreStats] = useState<any>(null)
  const [storeOrders, setStoreOrders] = useState<any[]>([])
  const [loadingStats, setLoadingStats] = useState(false)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const storesPerPage = 10

  useEffect(() => {
    loadStores()
  }, [statusFilter, activeFilter, currentPage])

  const loadStores = async () => {
    const cacheKey = `admin_store_approval_${currentPage}_${statusFilter}_${activeFilter}`
    
    // Intentar cargar desde cache primero
    const cachedStores = getCacheStale<PendingStore[]>(cacheKey)
    if (cachedStores && cachedStores.length > 0) {
      setAllStores(cachedStores)
      setStores(cachedStores)
      setLoading(false)
    }

    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: storesPerPage.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(activeFilter !== 'all' && { isActive: activeFilter === 'active' ? 'true' : 'false' })
      })

      const response = await api.get(`/admin/stores?${params}`)
      const storesData = response.data.data || []
      setAllStores(storesData)
      setStores(storesData)
      setTotalPages(response.data.pagination?.totalPages || 1)
      
      // Guardar en cache (2 minutos de TTL)
      setCache(cacheKey, storesData, 2 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading stores:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStores) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar los negocios')
      setStores([])
      setAllStores([])
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (storeId: string) => {
    setProcessing(storeId)
    try {
      await api.approveStore(storeId)
      toast.success('Negocio aprobado exitosamente')
      await loadStores()
    } catch (error: any) {
      console.error('Error approving store:', error)
      toast.error(error.response?.data?.message || 'Error al aprobar el negocio')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async () => {
    if (!selectedStore) return

    setProcessing(selectedStore.id)
    try {
      await api.rejectStore(selectedStore.id, rejectReason || undefined)
      toast.success('Negocio rechazado')
      setShowRejectModal(false)
      setSelectedStore(null)
      setRejectReason('')
      await loadStores()
    } catch (error: any) {
      console.error('Error rejecting store:', error)
      toast.error(error.response?.data?.message || 'Error al rechazar el negocio')
    } finally {
      setProcessing(null)
    }
  }

  const handleSuspend = async (storeId: string) => {
    if (!confirm('¿Estás seguro de que quieres suspender este negocio?')) return

    setProcessing(storeId)
    try {
      await api.suspendStore(storeId, 'Suspensión administrativa')
      toast.success('Negocio suspendido')
      await loadStores()
    } catch (error: any) {
      console.error('Error suspending store:', error)
      toast.error('Error al suspender el negocio')
    } finally {
      setProcessing(null)
    }
  }

  const handleActivate = async (storeId: string) => {
    setProcessing(storeId)
    try {
      await api.activateStore(storeId)
      toast.success('Negocio activado')
      await loadStores()
    } catch (error: any) {
      console.error('Error activating store:', error)
      toast.error('Error al activar el negocio')
    } finally {
      setProcessing(null)
    }
  }

  const handleDelete = async (storeId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este negocio? Esta acción no se puede deshacer.')) return

    setProcessing(storeId)
    try {
      await api.delete(`/admin/stores/${storeId}`)
      toast.success('Negocio eliminado exitosamente')
      await loadStores()
    } catch (error: any) {
      console.error('Error deleting store:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar el negocio')
    } finally {
      setProcessing(null)
    }
  }

  const loadStoreStats = async (storeId: string) => {
    setSelectedStore(stores.find(s => s.id === storeId) || null)
    setLoadingStats(true)
    try {
      const response = await api.get(`/admin/stores/${storeId}/stats`)
      setStoreStats(response.data)
      setShowStatsModal(true)
    } catch (error: any) {
      console.error('Error loading store estadísticas:', error)
      toast.error('Error al cargar estadísticas del negocio')
    } finally {
      setLoadingStats(false)
    }
  }

  const loadStoreOrders = async (storeId: string) => {
    setSelectedStore(stores.find(s => s.id === storeId) || null)
    setLoadingOrders(true)
    try {
      const response = await api.getAdminOrders({ storeId, limit: 50 })
      setStoreOrders(response.data?.data || [])
      setShowOrdersModal(true)
    } catch (error: any) {
      console.error('Error loading store orders:', error)
      toast.error('Error al cargar pedidos del negocio')
    } finally {
      setLoadingOrders(false)
    }
  }

  const openRejectModal = (store: PendingStore) => {
    setSelectedStore(store)
    setShowRejectModal(true)
  }

  const openDetailsModal = (store: PendingStore) => {
    navigate(`/admin/stores/${store.id}`)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-green-300'
      case 'pending': return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border-yellow-300'
      case 'rejected': return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-red-300'
      case 'suspended': return 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 border-orange-300'
      default: return 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-gray-300'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'pending': return <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'rejected': return <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
      case 'suspended': return <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" />
      default: return <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
    }
  }

  const filteredStores = stores.filter(store => {
    const matchesSearch = 
      store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.owner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.address.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  if (loading && stores.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando negocios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      {/* Header */}
      <AdminNavHeader
        title="Aprobación de Negocios"
        subtitle={statusFilter === 'pending' 
          ? `${stores.length} solicitudes pendientes`
          : `${allStores.length} negocios totales`}
      />

      {/* Filters */}
      <div className="bg-white/90 backdrop-blur-2xl shadow-lg border-b-2 border-gray-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-5">
          {/* Search Bar */}
          <div className="mb-4 sm:mb-5">
            <div className="relative max-w-md sm:max-w-lg lg:max-w-xl">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar negocios, propietarios, direcciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-12 lg:pl-14 pr-4 py-3 sm:py-3.5 lg:py-4 bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 text-sm sm:text-base font-medium shadow-sm hover:shadow-md placeholder-gray-400"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap sm:flex-nowrap gap-2.5 sm:gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any)
                setCurrentPage(1)
              }}
              className="flex-1 sm:flex-shrink-0 min-w-[140px] sm:min-w-[160px] px-4 sm:px-5 py-2.5 sm:py-3 bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobados</option>
              <option value="rejected">Rechazados</option>
              <option value="suspended">Suspendidos</option>
            </select>

            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as any)
                setCurrentPage(1)
              }}
              className="flex-1 sm:flex-shrink-0 min-w-[120px] sm:min-w-[140px] px-4 sm:px-5 py-2.5 sm:py-3 bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>

            {(searchTerm || statusFilter !== 'all' || activeFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('pending')
                  setActiveFilter('all')
                  setCurrentPage(1)
                }}
                className="flex-shrink-0 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl active:scale-95"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 safe-area-bottom">
        <div className="max-w-7xl mx-auto">
          {filteredStores.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-gray-200/60 p-6 sm:p-12 max-w-lg mx-auto">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                  <Store className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No hay negocios</h3>
                <p className="text-sm sm:text-base text-gray-600 font-medium">
                {statusFilter === 'pending' 
                  ? 'No hay solicitudes pendientes de aprobación'
                  : 'No se encontraron negocios con los filtros seleccionados'}
              </p>
              </div>
            </div>
          ) : (
            <>
              {/* Table View - Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Negocio</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Propietario</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Contacto</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Estado</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredStores.map((store) => (
                      <tr key={store.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 mr-3">
                              <Store className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">{store.name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[200px]">{store.description || 'Sin descripción'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 font-medium truncate">{store.owner.name}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[180px]">{store.owner.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 truncate flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span>{store.phone || 'No especificado'}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[150px] flex items-center gap-1.5 mt-1">
                            <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span>{store.email || 'No especificado'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor((store as any).status || 'pending')}`}>
                            {getStatusIcon((store as any).status || 'pending')}
                            <span className="capitalize">{(store as any).status || 'pending'}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(store.created_at).toLocaleDateString('es-DO', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openDetailsModal(store)}
                              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors shadow-sm hover:shadow-md"
                              title="Ver Detalles"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {(store as any).status === 'approved' && (
                              <>
                                <button
                                  onClick={() => loadStoreStats(store.id)}
                                  disabled={loadingStats}
                                  className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors shadow-sm hover:shadow-md disabled:opacity-50"
                                  title="Ver Estadísticas"
                                >
                                  <BarChart3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => loadStoreOrders(store.id)}
                                  disabled={loadingOrders}
                                  className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors shadow-sm hover:shadow-md disabled:opacity-50"
                                  title="Ver Pedidos"
                                >
                                  <Package className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {(store as any).status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(store.id)}
                                  disabled={processing === store.id}
                                  className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
                                  title="Aprobar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openRejectModal(store)}
                                  disabled={processing === store.id}
                                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
                                  title="Rechazar"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(store.id)}
                                  disabled={processing === store.id}
                                  className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {(store as any).status === 'approved' && (
                              <>
                                {(store as any).is_active ? (
                                  <button
                                    onClick={() => handleSuspend(store.id)}
                                    disabled={processing === store.id}
                                    className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Suspender"
                                  >
                                    <AlertCircle className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivate(store.id)}
                                    disabled={processing === store.id}
                                    className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Activar"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                            {(store as any).status === 'suspended' && (
                              <button
                                onClick={() => handleActivate(store.id)}
                                disabled={processing === store.id}
                                className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Reactivar"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Card View - Mobile/Tablet */}
              <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                {filteredStores.map((store) => (
                <div key={store.id} className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col group">
                {/* Header */}
                <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-white border-b-2 border-gray-200/50">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-xl flex-shrink-0 ring-2 ring-primary-200 group-hover:scale-110 transition-transform duration-300">
                      <Store className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight group-hover:text-primary-600 transition-colors truncate">{store.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{store.description || 'Sin descripción'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold border-2 shadow-sm ${getStatusColor((store as any).status || 'pending')}`}>
                      {getStatusIcon((store as any).status || 'pending')}
                      <span className="capitalize">{(store as any).status || 'pending'}</span>
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6 lg:p-7 space-y-4 sm:space-y-5 flex-grow">
                  {/* Owner Info */}
                  <div className="bg-gray-50 rounded-2xl p-4 sm:p-5 border border-gray-100">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-2">
                        <User className="w-4 h-4 text-primary-600" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900">Propietario</h4>
                    </div>
                    <div className="space-y-2.5 ml-10">
                      <div className="flex items-center text-gray-700">
                        <Mail className="w-4 h-4 mr-2.5 text-gray-400 flex-shrink-0" />
                        <span className="text-sm sm:text-base truncate break-all">{store.owner.email}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Phone className="w-4 h-4 mr-2.5 text-gray-400 flex-shrink-0" />
                        <span className="text-sm sm:text-base truncate">{store.owner.phone || 'No especificado'}</span>
                      </div>
                      <div className="flex items-center text-gray-900">
                        <span className="text-sm sm:text-base font-semibold truncate">{store.owner.name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Store Info */}
                  <div className="space-y-3 text-sm sm:text-base">
                    <div className="flex items-start text-gray-700">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 text-gray-400 flex-shrink-0" />
                      <span className="leading-relaxed break-words flex-1">{store.address}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{store.phone || 'No especificado'}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate break-all">{store.email}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-3 text-gray-400 flex-shrink-0" />
                      <span className="text-sm sm:text-base">
                        {new Date(store.created_at).toLocaleDateString('es-DO', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Services */}
                  <div className="flex flex-wrap gap-2">
                    {store.offers_delivery && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        Entrega
                      </span>
                    )}
                    {store.offers_kitchen_service && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                        Cocina
                      </span>
                    )}
                    {store.delivery_fee > 0 && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-green-50 text-green-700 border border-green-200">
                        Entrega: ${store.delivery_fee}
                      </span>
                    )}
                    {store.min_order_amount > 0 && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                        Mín: ${store.min_order_amount}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="pt-4 sm:pt-5 border-t-2 border-gray-200 flex-shrink-0">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 mb-2.5 sm:mb-3">
                      {/* Ver Detalles Button - Always visible */}
                      <button
                        onClick={() => openDetailsModal(store)}
                        className="flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-gray-100 hover:to-gray-200 transition-all duration-200 active:scale-95 border-2 border-gray-200 shadow-sm hover:shadow-md"
                      >
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 flex-shrink-0" />
                        <span className="hidden sm:inline">Detalles</span>
                        <span className="sm:hidden">Ver</span>
                      </button>
                      {(store as any).status === 'approved' && (
                        <>
                          <button
                            onClick={() => loadStoreStats(store.id)}
                            disabled={loadingStats}
                            className="flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-blue-100 hover:to-blue-200 transition-all duration-200 active:scale-95 border-2 border-blue-200 shadow-sm hover:shadow-md disabled:opacity-50"
                          >
                            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 flex-shrink-0" />
                            <span className="hidden sm:inline">Estadísticas</span>
                            <span className="sm:hidden">Estad.</span>
                          </button>
                          <button
                            onClick={() => loadStoreOrders(store.id)}
                            disabled={loadingOrders}
                            className="flex items-center justify-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-br from-purple-50 to-purple-100 text-purple-700 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-purple-100 hover:to-purple-200 transition-all duration-200 active:scale-95 border-2 border-purple-200 shadow-sm hover:shadow-md disabled:opacity-50"
                          >
                            <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 flex-shrink-0" />
                            <span className="hidden sm:inline">Pedidos</span>
                            <span className="sm:hidden">Orders</span>
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full">
                      
                      {/* Conditional Action Buttons */}
                      {(store as any).status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(store.id)}
                            disabled={processing === store.id}
                              className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-lg hover:shadow-xl min-w-[100px]"
                          >
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                            <span className="whitespace-nowrap">{processing === store.id ? 'Aprobando...' : 'Aprobar'}</span>
                          </button>
                          <button
                            onClick={() => openRejectModal(store)}
                            disabled={processing === store.id}
                              className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-lg hover:shadow-xl min-w-[100px]"
                          >
                            <XCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                            <span className="whitespace-nowrap">Rechazar</span>
                          </button>
                            <button
                              onClick={() => handleDelete(store.id)}
                              disabled={processing === store.id}
                              className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-lg hover:shadow-xl min-w-[100px]"
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                              <span className="whitespace-nowrap">Eliminar</span>
                            </button>
                        </>
                      )}
                      
                      {(store as any).status === 'approved' && (
                        <>
                          {(store as any).is_active ? (
                            <button
                              onClick={() => handleSuspend(store.id)}
                              disabled={processing === store.id}
                              className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 bg-yellow-500 text-white rounded-xl lg:rounded-2xl text-sm sm:text-base font-semibold hover:bg-yellow-600 active:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow-md min-w-[100px]"
                            >
                              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                              <span className="whitespace-nowrap">Suspender</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(store.id)}
                              disabled={processing === store.id}
                              className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 bg-green-500 text-white rounded-xl lg:rounded-2xl text-sm sm:text-base font-semibold hover:bg-green-600 active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow-md min-w-[100px]"
                            >
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                              <span className="whitespace-nowrap">Activar</span>
                            </button>
                          )}
                        </>
                      )}
                      
                      {(store as any).status === 'suspended' && (
                        <button
                          onClick={() => handleActivate(store.id)}
                          disabled={processing === store.id}
                          className="w-full sm:flex-1 flex items-center justify-center px-4 sm:px-5 py-3 sm:py-3.5 bg-green-500 text-white rounded-xl lg:rounded-2xl text-sm sm:text-base font-semibold hover:bg-green-600 active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow-md min-w-[100px]"
                        >
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                          <span className="whitespace-nowrap">Reactivar</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 sm:mt-8 lg:mt-10 flex items-center justify-center gap-3 sm:gap-4 lg:gap-6">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-xl lg:rounded-2xl text-sm sm:text-base text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors active:scale-95 shadow-sm font-medium"
              >
                ← Anterior
              </button>
              <span className="text-sm sm:text-base lg:text-lg font-medium text-gray-700 bg-white/90 backdrop-blur-sm px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-xl lg:rounded-2xl shadow-sm">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 sm:px-5 lg:px-6 py-2.5 sm:py-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-xl lg:rounded-2xl text-sm sm:text-base text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors active:scale-95 shadow-sm font-medium"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-md lg:max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between mb-5 sm:mb-6 lg:mb-8">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Rechazar Negocio</h2>
                <button
                  onClick={() => {
                    setShowRejectModal(false)
                    setSelectedStore(null)
                    setRejectReason('')
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="mb-5 sm:mb-6">
                <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-3 sm:mb-4">
                  Estás a punto de rechazar la solicitud de <strong>{selectedStore.name}</strong>
                </p>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2 sm:mb-3">
                  Razón del rechazo (opcional)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl lg:rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  placeholder="Ej: Documentación incompleta, información incorrecta, etc."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false)
                    setSelectedStore(null)
                    setRejectReason('')
                  }}
                  className="flex-1 py-3 sm:py-3.5 px-5 bg-gray-100 text-gray-700 rounded-xl lg:rounded-2xl hover:bg-gray-200 transition-colors active:scale-95 text-sm sm:text-base font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing === selectedStore.id}
                  className="flex-1 py-3 sm:py-3.5 px-5 bg-red-600 text-white rounded-xl lg:rounded-2xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95 text-sm sm:text-base font-medium"
                >
                  {processing === selectedStore.id ? 'Rechazando...' : 'Rechazar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal - Removed, now using full page at /admin/stores/:storeId */}

      {/* Modal de Estadísticas */}
      {showStatsModal && selectedStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 mr-2 text-primary-600" />
                  Estadísticas del Negocio
                </h2>
                      <button
                        onClick={() => {
                    setShowStatsModal(false)
                    setStoreStats(null)
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                  <X className="w-5 h-5" />
                  </button>
              </div>

              {loadingStats ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Cargando estadísticas...</p>
                  </div>
              ) : storeStats ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-blue-200 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-blue-700 mb-1">{storeStats.totalOrders || 0}</div>
                    <div className="text-xs sm:text-sm text-blue-600 font-medium">Total Pedidos</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-green-200 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-green-700 mb-1">RD${(storeStats.totalRevenue || 0).toLocaleString()}</div>
                    <div className="text-xs sm:text-sm text-green-600 font-medium">Ingresos Totales</div>
                </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-purple-200 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-purple-700 mb-1">{storeStats.totalProducts || 0}</div>
                    <div className="text-xs sm:text-sm text-purple-600 font-medium">Productos</div>
                    </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-orange-200 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-orange-700 mb-1">{storeStats.totalEmployees || 0}</div>
                    <div className="text-xs sm:text-sm text-orange-600 font-medium">Empleados</div>
                    </div>
                    </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No hay estadísticas disponibles</p>
                  </div>
              )}
                </div>
                      </div>
                      </div>
      )}

      {/* Orders Modal */}
      {showOrdersModal && selectedStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <Package className="w-6 h-6 sm:w-7 sm:h-7 mr-2 text-primary-600" />
                  Pedidos del Negocio
                </h2>
                <button
                  onClick={() => {
                    setShowOrdersModal(false)
                    setStoreOrders([])
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                      </div>

              {loadingOrders ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Cargando pedidos...</p>
                        </div>
              ) : storeOrders.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {storeOrders.map((order: any) => (
                    <div key={order.id} className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-gray-200 hover:shadow-lg transition-all duration-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm font-bold text-gray-600">Pedido #{order.id.slice(0, 8)}</span>
                          <span className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-bold ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="text-sm sm:text-base font-bold text-gray-900">
                          RD${(order.total_amount || 0).toLocaleString()}
                      </div>
                        </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('es-DO', {
                              year: 'numeric',
                          month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                        </div>
                      </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No hay pedidos disponibles</p>
                    </div>
                  )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminStoreApproval
